/**
 * The `bigquery-sql/append` tool takes two aligned params: `columns` (the
 * column names) and `rows` (positional cell values, one array per row, each
 * cell lined up with `columns`). The editor keeps them in that shape and edits
 * them together — a column added or removed has to move the matching cell in
 * every row, or the positions drift.
 *
 * Cells are authored as template strings (literal text and/or `{{ref}}` tokens),
 * the same form every other tool input uses; they are resolved to values by the
 * runner before the tool sees them and never spliced into SQL (`append` uses
 * BigQuery's streaming insert API).
 */

export type AppendDraft = {
  readonly columns: string[];
  /** Row-major cell templates, each row the same length as `columns`. */
  readonly rows: string[][];
};

const asArray = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : [];

/** Reads the saved `columns` param back into editable strings. */
export const parseColumns = (value: unknown): string[] =>
  asArray(value).map((c) => (typeof c === 'string' ? c : ''));

/**
 * Reads the saved `rows` param, normalizing every row to exactly `columnCount`
 * string cells so the grid is rectangular even if an authored payload was
 * ragged. Non-string cells (a number/bool typed elsewhere) render as their text
 * so they can be edited, not dropped.
 */
export const parseRows = (value: unknown, columnCount: number): string[][] =>
  asArray(value).map((row) => {
    const cells = asArray(row);
    return Array.from({ length: columnCount }, (_, i) => {
      const cell = cells[i];
      return cell === null || cell === undefined ? '' : String(cell);
    });
  });

/** The current draft as the editor holds it, read from the two params. */
export const readAppendDraft = (
  params: Readonly<Record<string, unknown>>
): AppendDraft => {
  const columns = parseColumns(params['columns']);
  return { columns, rows: parseRows(params['rows'], columns.length) };
};

const withColumnAdded = (draft: AppendDraft): AppendDraft => ({
  columns: [...draft.columns, ''],
  rows: draft.rows.map((row) => [...row, '']),
});

const withColumnRemoved = (draft: AppendDraft, index: number): AppendDraft => ({
  columns: draft.columns.filter((_, i) => i !== index),
  rows: draft.rows.map((row) => row.filter((_, i) => i !== index)),
});

const withColumnRenamed = (
  draft: AppendDraft,
  index: number,
  name: string
): AppendDraft => ({
  ...draft,
  columns: draft.columns.map((c, i) => (i === index ? name : c)),
});

const withRowAdded = (draft: AppendDraft): AppendDraft => ({
  ...draft,
  rows: [...draft.rows, draft.columns.map(() => '')],
});

const withRowRemoved = (draft: AppendDraft, index: number): AppendDraft => ({
  ...draft,
  rows: draft.rows.filter((_, i) => i !== index),
});

const withCellSet = (
  draft: AppendDraft,
  rowIndex: number,
  colIndex: number,
  template: string
): AppendDraft => ({
  ...draft,
  rows: draft.rows.map((row, r) =>
    r === rowIndex
      ? row.map((cell, c) => (c === colIndex ? template : cell))
      : row
  ),
});

export const AppendDraftOps = {
  addColumn: withColumnAdded,
  removeColumn: withColumnRemoved,
  renameColumn: withColumnRenamed,
  addRow: withRowAdded,
  removeRow: withRowRemoved,
  setCell: withCellSet,
};

/** The two params a draft serializes to, ready for `setValues`. */
export const serializeAppendDraft = (
  draft: AppendDraft
): { columns: string[]; rows: string[][] } => ({
  columns: draft.columns,
  rows: draft.rows,
});
